import {
  AnimatedSelect,
  AnimatedSelectProps,
  MapSection,
  MapSectionProps,
  TeamCardsSection,
  TeamCardsSectionProps,
  ValuesSection,
  ValuesSectionProps,
} from '@rimactechnology/rimac-ui-lib';

import {
  CompanyCampusQuery,
  FooterQuery,
  FormQuery,
  HeroVideoSectionFragment,
  LargeImageSectionFragment,
  MapSectionFragment,
  NavigationQuery,
  NewsroomFragment,
  SeoFragment,
  SliderImageSectionFragment,
  TeamCardsFragment,
  ValuesFragment,
} from '@/api';
import { Layout } from '@/components/layout/Layout';
import NewsroomSection from '@/components/newsroom/NewsroomSection';
import { Seo } from '@/components/Seo';
import HeroVideoSection from '@/components/shared/HeroVideoSection';
import LargeImage from '@/components/shared/LargeImage';
import SliderImage from '@/components/shared/SliderImage';
import getCdlPublicId from '@/hooks/useCdlPublicId';

type Props = {
  seo?: SeoFragment | null;
  hero?: HeroVideoSectionFragment | null;
  sliderImageSection?: SliderImageSectionFragment | null;
  values?: ValuesFragment | null;
  campus: CompanyCampusQuery;
  mapSection?: MapSectionFragment | null;
  teamCardsSection?: TeamCardsFragment | null;
  largeImageSection?: LargeImageSectionFragment | null;
  newsroom?: NewsroomFragment | null;
  formData: FormQuery;
  footerData: FooterQuery;
  headerData: NavigationQuery;
};

export type guidelinesThemeType = 'light' | 'dark';

export const CompanyPage = ({
  seo,
  hero,
  sliderImageSection,
  values,
  campus,
  mapSection,
  teamCardsSection,
  largeImageSection,
  newsroom,
  formData,
  footerData,
  headerData,
}: Props) => {
  const campusAttributes = campus.company?.data?.attributes?.Campus;

  const valuesProps: ValuesSectionProps = {
    title: values?.title || '',
    anchor: values?.anchor || undefined,
    cards: (values?.cards || []).map((card, index) => ({
      index: index || 1,
      title: card?.title || '',
      icon: getCdlPublicId(card?.icon.data?.attributes?.url || ''),
      text: card?.description || '',
    })),
  };

  const animatedSelectProps: AnimatedSelectProps = {
    anchor: campus.company?.data?.attributes?.CampusAnchor || undefined,
    selectOptions: (campusAttributes || []).map((campus, index) => ({
      parentIndex: index || 1,
      image: {
        imgSrc: getCdlPublicId(
          campus?.image?.img_desktop.data?.attributes?.url || '',
        ),
        imgSrcMobile: getCdlPublicId(
          campus?.image?.img_mobile?.data?.attributes?.url || undefined,
        ),
        alt: campus?.image?.alt || '',
        blurHash:
          campus?.image?.img_desktop.data?.attributes?.blurhash ||
          'LAS?DV%M_3D%t7fQofj[~qM{4n-;',
      },
      title: campus?.Title || '',
      description: campus?.Text || '',
      stats: (campus?.spec || []).map((stat) => ({
        label: stat?.label || '',
        value: stat?.value || '',
      })),
    })),
  };

  const mapSectionProps: MapSectionProps = {
    title: mapSection?.title || '',
    anchor: mapSection?.anchor || undefined,
    parentIndex: 1,
    mapItems: [
      {
        mapImages: {
          leftMap: {
            title: mapSection?.leftMapTitle || '',
            mapImage: {
              imgSrc: getCdlPublicId(
                mapSection?.leftMapImage?.data?.attributes?.url || '',
              ),
              imgSrcMobile: getCdlPublicId(
                mapSection?.leftMapImageMobile?.data?.attributes?.url || '',
              ),
              alt: mapSection?.leftMapTitle || '',
              blurHash:
                mapSection?.leftMapImage?.data?.attributes?.blurhash ||
                'LAS?DV%M_3D%t7fQofj[~qM{4n-;',
            },
          },
          rightMap: {
            title: mapSection?.rightMapTitle || '',
            mapImage: {
              imgSrc: getCdlPublicId(
                mapSection?.rightMapImage?.data?.attributes?.url || '',
              ),
              imgSrcMobile: getCdlPublicId(
                mapSection?.rightMapImageMobile?.data?.attributes?.url || '',
              ),
              alt: mapSection?.rightMapTitle || '',
              blurHash:
                mapSection?.rightMapImage?.data?.attributes?.blurhash ||
                'LAS?DV%M_3D%t7fQofj[~qM{4n-;',
            },
          },
        },
      },
    ],
  };

  const teamCardsSectionProps: TeamCardsSectionProps = {
    title: teamCardsSection?.title || '',
    anchor: teamCardsSection?.anchor || undefined,
    teammates: (teamCardsSection?.teammates || []).map((person, index) => ({
      parentIndex: index || 1,
      name: person?.name || '',
      position: person?.position || '',
      image: {
        imgSrc: getCdlPublicId(
          person?.img_desktop?.data?.attributes?.url || '',
        ),
        imgSrcMobile: getCdlPublicId(
          person?.img_mobile?.data?.attributes?.url || undefined,
        ),
        alt: person?.alt || person?.name || '',
        blurHash: person?.img_desktop?.data?.attributes?.blurhash || '12312312',
      },
    })),
  };

  return (
    <Layout formData={formData} footerData={footerData} headerData={headerData}>
      <Seo seo={seo} />
      <HeroVideoSection hero={hero} titleMaxWidth={740} />
      <SliderImage
        sliderImageSection={sliderImageSection}
        nonLazyLoadImageCount={0}
      />
      <ValuesSection {...valuesProps} />
      <AnimatedSelect {...animatedSelectProps} />
      <MapSection {...mapSectionProps} />
      <TeamCardsSection {...teamCardsSectionProps} />
      <LargeImage
        textCenter
        descriptionMaxWidth={720}
        largeImageSection={largeImageSection}
      />
      <NewsroomSection strapiData={newsroom} />
    </Layout>
  );
};
